import React, { useEffect, useState } from "react";
import Layout from "../Layout/Layout";
import "../css/add-new-message.css";
import ChatFriendList from "../Components/ChatFriendList";
import { MessageBox } from "../Components/Chat/ChatInput";
import CustomizeModal from "../Components/CustomizeModal";
import ChatHeadCard from "../Components/ChatHeadCard";
import { db } from "../Utils/firebase";
import ChatMessageBox from "../Components/Chat/ChatMessageBox";
import { getProfile } from "../actions/login";
import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { api } from "../actions/api";
import { toast } from "react-toastify";
import { FIREBASE_CONVERSATIONS } from "../Utils/constant";
import { generateThumbnail } from "../helpers/GenerateThumbnailHelper";
import convertToMp3 from "../Utils/convertToMp3";
import { sendFcmNotificationToTopic } from "../helpers/NotificationHelper";
import { buildUnreadUserIds } from "../Utils/chatUnread";

export default function AddNewMessage({ setShowAddMessage, onChatCreated }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [groupChatData, setGroupChatData] = useState({
    title: "",
    description: "",
  });
  const [disabledEditorButton, setDisabledEditorButton] = useState(false);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const profile = await getProfile();
        setUserProfile(profile);
      } catch (error) {
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, []);

  useEffect(() => {
    if (!userProfile?.id) return;

    const fetchAllUsers = async () => {
      try {
        const response = await api.get("user/list");
        const usersList = (response.data?.data?.users || [])
          .filter((user) => String(user.id) !== String(userProfile.id))
          .map((user) => ({
            id: user.id,
            name: user.full_name,
            profileImage: user.image,
            email: user.email,
            disabled_at: user.disabled_at,
          }));

        setUsers(usersList);
      } catch (error) {
        console.error("Error fetching users:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllUsers();
  }, [userProfile?.id]);

  const handleSendMessage = async (content, file) => {
    let newMessageId = null;
    let actualConversationId = null;
    if (!userProfile) return;

    if (!selectedUsers.length) {
      toast.error("Please select users");
      return;
    }

    setLoading(true);

    let fileData = null;

    if (file) {
      fileData = await handleSendFile(file);
    }

    try {
      const userId = String(userProfile.id);

      const constructMessage = (visibleTo) => {
        const baseMessage = {
          content,
          senderId: userId,
          senderName: userProfile.full_name,
          senderProfileImage: userProfile.image,
          type: "text",
          timestamp: serverTimestamp(),
          reactions: [],
          metadata: {},
          isRead: null,
          isFile: !!fileData,
          visibleTo,
        };

        if (fileData) {
          baseMessage.file = {
            thumbnail: fileData.media_url || null,
            url:
              fileData.video_url ||
              fileData.media_url ||
              fileData.audio_url ||
              null,
            type: fileData.type || null,
          };
        }

        return baseMessage;
      };

      // PRIVATE CHAT
      if (selectedUsers.length === 1) {
        const otherUserId = String(selectedUsers[0].id);
        const sortedIds = [userId, otherUserId].sort(
          (a, b) => Number(a) - Number(b),
        );
        actualConversationId = `${sortedIds[0]}_${sortedIds[1]}`;
        setConversationId(actualConversationId);

        const convoRef = doc(db, FIREBASE_CONVERSATIONS, actualConversationId);
        const convoSnap = await getDoc(convoRef);

        const participants = [userId, otherUserId];
        const participantDetails = {
          [userId]: {
            name: userProfile.full_name,
            email: userProfile.email,
            profileImage: userProfile.image,
          },
          [otherUserId]: {
            name: selectedUsers[0].name,
            email: selectedUsers[0].email,
            profileImage: selectedUsers[0].profileImage,
          },
        };

        if (!convoSnap.exists()) {
          await setDoc(convoRef, {
            conversationId: actualConversationId,
            participants,
            participantDetails,
            lastMessage: content,
            lastMessageSenderId: userId,
            lastMessageTime: serverTimestamp(),
            unreadCount: { [userId]: 0, [otherUserId]: 1 },
            unreadUserIds: [otherUserId],
            type: "direct",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            isActive: true,
            visibleTo: participants,
          });
        } else {
          const data = convoSnap.data();
          const updatedUnread = {
            ...data.unreadCount,
            [userId]: 0,
            [otherUserId]: (data.unreadCount?.[otherUserId] || 0) + 1,
          };
          await updateDoc(convoRef, {
            lastMessage: content,
            lastMessageSenderId: userId,
            lastMessageTime: serverTimestamp(),
            unreadCount: updatedUnread,
            unreadUserIds: buildUnreadUserIds(updatedUnread),
            updatedAt: serverTimestamp(),
            // Either side may have removed themselves via "delete for me";
            // a new message must make the conversation visible to both again.
            visibleTo: arrayUnion(...participants),
          });
        }

        const messageRef = collection(
          db,
          FIREBASE_CONVERSATIONS,
          actualConversationId,
          "messages",
        );

        const newMessageRef = await addDoc(
          messageRef,
          constructMessage(participants),
        );
        newMessageId = newMessageRef.id;
      }

      // GROUP CHAT
      if (selectedUsers.length > 1) {
        const participantIds = [
          userId,
          ...selectedUsers.map((u) => String(u.id)),
        ];

        const participantDetails = {
          [userId]: {
            name: userProfile.full_name,
            email: userProfile.email,
            profileImage: userProfile.image,
          },
        };

        selectedUsers.forEach((u) => {
          participantDetails[String(u.id)] = {
            name: u.name,
            email: u.email,
            profileImage: u.profileImage,
          };
        });

        const groupUnreadCount = Object.fromEntries(
          participantIds.map((id) => [id, id === userId ? 0 : 1]),
        );

        // Create group conversation
        const convoRef = await addDoc(collection(db, FIREBASE_CONVERSATIONS), {
          participants: participantIds,
          participantDetails,
          type: "group",
          lastMessage: content,
          lastMessageSenderId: userId,
          lastMessageTime: serverTimestamp(),
          unreadCount: groupUnreadCount,
          unreadUserIds: buildUnreadUserIds(groupUnreadCount),
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          isActive: true,
          metadata: {
            adminList: [userId],
            createdBy: userId,
            groupDescription:
              groupChatData.description || "Group Chat Description",
            groupName: groupChatData.title || "Group Chat",
          },
          visibleTo: participantIds,
        });

        actualConversationId = convoRef.id;

        await updateDoc(convoRef, { conversationId: actualConversationId });
        setConversationId(actualConversationId);

        // Add first message
        const messageRef = collection(
          db,
          FIREBASE_CONVERSATIONS,
          convoRef.id,
          "messages",
        );
        const newMessageRef = await addDoc(
          messageRef,
          constructMessage(participantIds),
        );
        newMessageId = newMessageRef.id;
      }

      // Send Push Notification
      const isGroup = selectedUsers.length > 1;
      const notificationTitle = isGroup
        ? `${userProfile.full_name} created a new group: ${groupChatData.title || "Group Chat"}`
        : `${userProfile.full_name} sent you a message`;

      const notificationRecipients = [
        userId,
        ...selectedUsers.map((u) => String(u.id)),
      ];

      sendFcmNotificationToTopic({
        title: notificationTitle,
        body: content || (fileData ? "Attachment" : ""),
        conversationId: actualConversationId,
        isGroupChat: isGroup,
        recipientIds: notificationRecipients,
        senderId: userProfile.id,
        messageId: newMessageId || `${actualConversationId}_${Date.now()}`,
      });

      // Open the conversation we just messaged instead of closing back to the
      // empty state. Fall back to just closing when the parent doesn't pass a
      // handler (standalone /add-new-message route).
      if (typeof onChatCreated === "function") {
        onChatCreated(
          actualConversationId,
          isGroup ? "group" : "direct",
          isGroup ? "/icons/group-user.svg" : selectedUsers[0].profileImage,
          isGroup ? groupChatData.title || "Group Chat" : selectedUsers[0].name,
          isGroup ? selectedUsers.length + 1 : 2,
        );
      } else if (typeof setShowAddMessage === "function") {
        setShowAddMessage(false);
      }
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setLoading(false);
      setDisabledEditorButton(false);
    }
  };

  const chunkSize = 5 * 1024 * 1024; // 5MB per chunk

  function generateRandomString(length) {
    const chars =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    let result = "";
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  const uploadFileInChunks = async (file, fileId) => {
    const totalChunks = Math.ceil(file.size / chunkSize);

    for (let chunkNumber = 1; chunkNumber <= totalChunks; chunkNumber++) {
      const start = (chunkNumber - 1) * chunkSize;
      const end = Math.min(file.size, start + chunkSize);
      const chunk = file.slice(start, end);

      const formData = new FormData();
      formData.append("file_id", fileId);
      formData.append("chunk_number", chunkNumber);
      formData.append("total_chunks", totalChunks);
      formData.append("file_name", file.name);
      formData.append("chunk", chunk);
      await api.post("/upload-chunk", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    }
  };

  const handleSendFile = async (file) => {
    setLoading(true);
    setDisabledEditorButton(true);

    try {
      // Convert audio to MP3 before upload when required
      if (
        file &&
        file.type &&
        file.type.startsWith("audio") &&
        file.type !== "audio/mpeg"
      ) {
        try {
          const mp3File = await convertToMp3(file);
          if (mp3File) file = mp3File;
        } catch (err) {
          console.warn("convertToMp3 failed, uploading original file", err);
        }
      }
      const formData = new FormData();

      const uploadedVideoIds = [];

      try {
        if (file.type.startsWith("video")) {
          const fileId = generateRandomString(10);
          await uploadFileInChunks(file, fileId);
          uploadedVideoIds.push(fileId);

          // Generate thumbnail for mobile
          const thumbnail = await generateThumbnail(file);

          if (thumbnail) {
            formData.append("media", thumbnail);
          }
        } else if (file.type.startsWith("audio")) {
          formData.append("audio", file);
        } else {
          formData.append("media", file);
        }

        // 2. Append videoIds once after loop
        if (uploadedVideoIds.length > 0) {
          formData.append("videoIds", JSON.stringify(uploadedVideoIds));
        }
      } catch (e) {
        setLoading(false);
      }

      const response = await api.post("/user/chat/media/upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (!response.status == 200) {
        throw new Error("Upload failed");
      }

      const fileData = response.data;

      setLoading(false);
      return fileData;
    } catch (error) {
      console.error("Error uploading file:", error);
      setLoading(false);
    }
  };

  return (
    <>
      <div className="user-chats-card users-chat-card-components">
        <div className="d-flex w-100">
          <div className="flex-grow-1 flex-column chats-contents active-chat">
            <ChatHeadCard
              users={users}
              userProfile={userProfile}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
            />
            <div className="chat-container d-flex flex-column flex-grow-1">
              <div
                className="no-scrollbar mt-auto d-flex flex-column flex-grow-1"
                style={{ height: "0px", minHeight: "0px" }}
              >
                {/* chat body goes here*/}
              </div>
              {/* message box */}
              {/* <MessageBox onSendClick={handleSendMessage} /> */}
              <ChatMessageBox
                conversationId={conversationId}
                userProfile={userProfile}
                onSendClick={handleSendMessage}
                onSendFile={handleSendFile}
                disabledButton={disabledEditorButton}
                // onSendClick={(newContent) =>
                //     handleSaveEdit(
                //         messages.find((msg) => msg.isEditing)?.id,
                //         newContent
                //     )
                // }
                // initialText={
                //     messages.find((msg) => msg.isEditing)?.content || ""
                // }
                // isEditing={true}
                // onCancel={() =>
                //     handleCancelEdit(
                //         messages.find((msg) => msg.isEditing)?.id
                //     )
                // }
                // disabled={!isClientReady}
                // showFileUpload={false}
              />
            </div>
          </div>
        </div>
      </div>

      {/*customize modal */}
      <CustomizeModal
        selectedUsers={selectedUsers}
        onSave={({ title, description, members }) => {
          setSelectedUsers(members);
          setGroupChatData({ title, description });
        }}
        allUsers={users}
      />
    </>
  );
}
